import React from 'react';
import PropTypes from 'prop-types';

import { getProportionalFontSize, getVariantByTileSize } from 'display/common/utils/styles';

import ReactGridLayout from 'react-grid-layout';
import Velocity from 'velocity-animate';

import _ from 'lodash';

import enums from '../../config/enums';

import Medialayer from 'display/common/components/displayItems/items/MediaLayer.jsx';
import PinnedDisplayItem from 'display/common/model/entity/PinnedDisplayItem';

import * as DisplayItemUtils from 'display/common/components/displayItems/utils';

const BREAKPOINT_COLUMNS = { lg: 28, md: 12, sm: 2, xs: 1 };
const BREAKPOINT_THRESHOLDS = { lg: 900, md: 480, sm: 250, xs: 0 };
const DEFAULT_ROTATION_INTERVAL = 5;
const TRANSITION_DURATION = 500;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Display Item > Collage > Custom.
 *
 * Summary of responsibilities:
 *  - tracks queue of content items to rotate in at set interval
 *  - outputs wrapper elements for custom/collage collection.
 * </pre>
 *
 * @memberOf Common.Components.Modules
 * @namespace
 *
 * @return {Common.Components.Modules.Custom}
 */
class Custom extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.handleWindowResize = _.debounce(this.handleWindowResize.bind(this), 50);
    this.rotateTile = this.rotateTile.bind(this);
    this.updateTilePin = this.updateTilePin.bind(this);
    this.getActivePinnedMedia = this.getActivePinnedMedia.bind(this);
    this.getPinnedContent = this.getPinnedContent.bind(this);
    this.separatePinnedContent = this.separatePinnedContent.bind(this);
    this.getRotatableTiles = this.getRotatableTiles.bind(this);
    this.generateTileMap = this.generateTileMap.bind(this);
    this.generateDisplayItemFromPin = this.generateDisplayItemFromPin.bind(this);
    this.generateNextPinIdxMap = this.generateNextPinIdxMap.bind(this);
    this.getNextItemIdx = this.getNextItemIdx.bind(this);
    this.getItemByIndex = this.getItemByIndex.bind(this);
    this.getRandomTileIdx = this.getRandomTileIdx.bind(this);
    this.rotateContentTile = this.rotateContentTile.bind(this);
    this.rotatePinnedTile = this.rotatePinnedTile.bind(this);
    this.getTileIdFromIdx = this.getTileIdFromIdx.bind(this);
    this.animateTile = this.animateTile.bind(this);
    this.isPinnedTile = this.isPinnedTile.bind(this);
    this.isItemDisplayed = this.isItemDisplayed.bind(this);
    this.getTileById = this.getTileById.bind(this);
    this.setContainer = this.setContainer.bind(this);

    // Here we will store the reference to the DOM node of the GridLayout (container of the collage).
    // It will allow us to get the direct access tothe children DOM nodes (collage items), and apply the animation
    this.containerRef = React.createRef(null);

    // set layouts, w/backwards compatibility for pre md and sm layout builders
    // sort by 'x' 'y' coordinates for pinned content association
    const breakpointLayouts = addUIPropsToLayouts({
      lg: sortLayoutByCoords([...props.layouts.lg]),
      md: !_.isEmpty(props.layouts.md)
        ? sortLayoutByCoords([...props.layouts.md])
        : generateMediumLayout(props.layouts.lg.length, BREAKPOINT_COLUMNS.md),
      sm: !_.isEmpty(props.layouts.sm)
        ? sortLayoutByCoords([...props.layouts.sm])
        : generateSmallLayout(props.layouts.lg.length, BREAKPOINT_COLUMNS.sm),
      xs: generateSmallLayout(props.layouts.lg.length),
    });

    this.rotatableTiles = [];

    const { nonPinnedContent, pinnedContent } = this.separatePinnedContent(props.children);
    this.state = {
      items:              _.take(props.moduleOpts.shouldShowStickyContent ? nonPinnedContent : props.children, enums.CONTENT_CAP),
      pinnedContentItems: props.moduleOpts.shouldShowStickyContent ? pinnedContent : [],
      breakpointLayouts,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);
    const containerWidth = this.container.offsetWidth;
    const { columns, breakpoint } = getLayoutProps(containerWidth);
    const interval = parseFloat(this.props.moduleOpts.tileRotationInterval, 10) || DEFAULT_ROTATION_INTERVAL;
    const rotationInterval = setInterval(this.rotateTile, interval * 1000);
    const activeLayout = this.state.breakpointLayouts[breakpoint];

    this.setState({
      nextItemIdx:       Math.min(activeLayout.length, this.state.items.length), // index of next item not displayed yet
      nextPinnedIdxMap:  this.generateNextPinIdxMap(activeLayout),
      currentWidth:      containerWidth,
      tileMap:           this.generateTileMap(activeLayout, this.state.items),
      currentCols:       columns,
      currentBreakpoint: breakpoint,
      rotationInterval,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    clearInterval(this.state.rotationInterval);
  }

  componentDidUpdate(prevProps) {
    let pinnedContentItems = [];
    let items = [];
    let tileMap = this.state.tileMap;

    const nextPinnedIdxMap = this.state.nextPinnedIdxMap;
    if (prevProps.children !== this.props.children) {
      if (prevProps.newItems !== this.props.newItems && !_.isEmpty(this.props.newItems)) {
        items = [...this.state.items];

        // remove any items from state that have been removed from children due to CONTENT_CAP.
        items = _.intersectionBy(items, this.props.children, 'key');

        // separate pinned and non-pinned newItems if shouldShowStickyContent is enabled
        if (this.props.moduleOpts.shouldShowStickyContent) {
          const { nonPinnedContent, pinnedContent } = this.separatePinnedContent(this.props.newItems);

          // splice newer non-pinned content into items, so it will be next to rotate in
          items.splice(this.state.nextItemIdx % items.length, 0, ...nonPinnedContent);

          pinnedContentItems = pinnedContent.concat(this.state.pinnedContentItems);
          const self = this;
          pinnedContent.forEach((item) => {
            const tile = self.getCurrentLayout()[_.get(item, 'props.item.meta.pinned') - 1];
            if (!!tile) {
              nextPinnedIdxMap[tile.i] = 0; // reset to zero so that new pin shows on next rotation
              // if tile should not rotate, just replace content
              if (pinnedContent.length === 1 && this.getActivePinnedMedia(tile).length === 0) {
                tileMap = this.updateTilePin(tile.i, pinnedContent, 0);
              }
            }
          });
        } else {
          // splice newer content into items, so it will be next to rotate in
          items.splice(this.state.nextItemIdx % items.length, 0, ...this.props.newItems);
        }
      } else {
        if (this.props.moduleOpts.shouldShowStickyContent) {
          const { nonPinnedContent, pinnedContent } = this.separatePinnedContent(this.props.children);
          items = nonPinnedContent;
          pinnedContentItems = pinnedContent;
        } else {
          items = this.props.children;
        }
      }

      this.setState({ items, pinnedContentItems, nextPinnedIdxMap, tileMap });
    }
  }

  handleWindowResize() {
    const containerWidth = this.container.offsetWidth;
    const { columns, breakpoint } = getLayoutProps(containerWidth);
    if (breakpoint !== this.state.currentBreakpoint) {
      this.setState(
        {
          currentWidth:      containerWidth,
          currentBreakpoint: breakpoint,
          currentCols:       columns,
          tileMap:           this.generateTileMap(this.state.breakpointLayouts[breakpoint], this.state.items),
          nextPinnedIdxMap:  this.generateNextPinIdxMap(this.state.breakpointLayouts[breakpoint]),
        },
        () => {
          _.forEach(this.containerRef.current.children, (item, id) => {
            // set opacity on item if its tile was previously animated out
            if (item && _.includes(item.classList, 'velocity-animating')) {
              Velocity(item, { opacity: 1 }, { mobileHA: false }, TRANSITION_DURATION);
            }
          });
        }
      );
      // reset rotatableTiles
      this.rotatableTiles = [];
    } else {
      this.setState({ currentWidth: containerWidth });
    }
  }

  /* tile cannot rotate if:
     - content length is less than or equal to layout length
     - tile has a single piece of pinned content or media assigned to it
     - tile has no content to fill it (content length is less than layout length)
  */
  getRotatableTiles() {
    const activeLayout = this.state.breakpointLayouts[this.state.currentBreakpoint];
    if (_.isEmpty(this.rotatableTiles)) {
      this.rotatableTiles = _.reject(activeLayout, (tile, idx) => {
        // get pinned content for tile idx (position)
        const pinnedContentLength = this.getPinnedContent(idx).length;
        const activePinnedMediaLength = this.getActivePinnedMedia(tile).length;
        const totalPins = activePinnedMediaLength + pinnedContentLength;
        // reject if a tile only has one pin, or if there is no pin or content item for tile
        return totalPins === 1 || (totalPins === 0 && !this.state.tileMap[tile.i]);
      });
    }
    return this.rotatableTiles;
  }
  //  making this a class method so that I can mock it in collage.test.js
  getRandomTileIdx(tiles) {
    return _.random(0, tiles.length - 1);
  }

  rotateContentTile(tileIdx) {
    let { tileMap, items, currentBreakpoint } = this.state;
    const rotatableTiles = this.getRotatableTiles();

    const nextItemIdx = this.getNextItemIdx();
    const nextItem = this.getItemByIndex(nextItemIdx);

    const tileId = this.getTileIdFromIdx(tileIdx);
    tileMap = Object.assign({}, tileMap, { [tileId]: nextItem });

    //  remove the tile from the current list of rotatable tiles
    _.pullAt(rotatableTiles, [tileIdx]);
    this.animateTile(tileId, { tileMap, nextItemIdx });
  }
  rotatePinnedTile(tileIdx) {
    // handles both content and media pins
    let { nextPinnedIdxMap, tileMap, currentBreakpoint, breakpointLayouts } = this.state;
    const tileId = this.getTileIdFromIdx(tileIdx);
    let nextPinIdx = nextPinnedIdxMap[tileId];

    const tile = this.getTileById(tileId, breakpointLayouts[currentBreakpoint]);
    const activePinnedMedia = this.getActivePinnedMedia(tile);
    // pass index of tile in overall layout to check for pinned content in that position
    const pinnedContent = this.getPinnedContent(_.indexOf(breakpointLayouts[currentBreakpoint], tile));
    tileMap = this.updateTilePin(tileId, pinnedContent.concat(activePinnedMedia), nextPinIdx);

    // increment next pin index
    nextPinnedIdxMap = Object.assign({}, nextPinnedIdxMap, {
      [tileId]: (nextPinIdx += 1),
    });

    const rotatableTiles = this.getRotatableTiles();
    //  remove the tile from the current list of rotatable tiles
    _.pullAt(rotatableTiles, [tileIdx]);
    this.animateTile(tileId, { tileMap, nextPinnedIdxMap });
  }
  isPinnedTile(tile) {
    const activePins = this.getActivePinnedMedia(tile);
    return !_.isEmpty(activePins) || !_.isEmpty(this.getPinnedContent(_.indexOf(this.getCurrentLayout(), tile)));
  }
  getTileById(tileId, layout) {
    return _.find(layout, { i: tileId });
  }
  getTileIdFromIdx(tileIdx) {
    const rotatableTiles = this.getRotatableTiles();
    return rotatableTiles[tileIdx].i;
  }
  animateTile(tileId, updatedItemsState) {
    const tileDomNode = [].find.call(this.containerRef.current.children, (tile) => tile.dataset.tileId === tileId);
    // animate old item out, set new state, animate new item in
    // HEADS UP - mobileHA set to false so that RGL inline transforms are not overriden
    // if layout/breakpoint has changed during animation, don't set updatedItemsState/reference tileId, different layout could have totally different tiles.
    Velocity(tileDomNode, { opacity: 0 }, { mobileHA: false, duration: TRANSITION_DURATION }).then(() => {
      if (_.isEqual(_.keys(this.state.tileMap), _.keys(updatedItemsState.tileMap))) {
        this.setState(updatedItemsState, () => {
          Velocity(tileDomNode, { opacity: 1 }, { mobileHA: false, duration: TRANSITION_DURATION });
        });
      }
    });
  }
  rotateTile() {
    const rotatableTiles = this.getRotatableTiles();
    const pinnedTiles = _.filter(this.getCurrentLayout(), (tile) => this.isPinnedTile(tile));
    const hasEnoughContentToRotate = this.getCurrentLayout().length - pinnedTiles.length < this.state.items.length;

    if (_.isEmpty(rotatableTiles)) {
      return;
    }
    const tileIdx = this.getRandomTileIdx(rotatableTiles);
    if (this.isPinnedTile(rotatableTiles[tileIdx])) {
      this.rotatePinnedTile(tileIdx);
    } else if (hasEnoughContentToRotate) {
      this.rotateContentTile(tileIdx);
    }
  }

  updateTilePin(tileId, pins, nextPinIdx) {
    // pins here are both content, then media pins
    const nextPin = this.getNextPin(nextPinIdx, pins);
    return Object.assign({}, this.state.tileMap, {
      [tileId]: nextPin.hasOwnProperty('assets') ? this.generateDisplayItemFromPin(nextPin) : nextPin,
    });
  }

  getActivePinnedMedia(tile) {
    const tilePinIds = this.props.moduleOpts.pinsByTileId[tile.i];
    const activePins = [];
    _.forEach(tilePinIds, (pinId) => {
      const pin = this.props.moduleOpts.pinnedContent[pinId];
      if (pin.isActive) activePins.push(pin);
    });
    return activePins;
  }

  getPinnedContent(tileIdx) {
    return _.filter(
      this.state.pinnedContentItems,
      (displayItem) => _.get(displayItem, 'props.item.meta.pinned') - 1 === tileIdx
    );
  }

  separatePinnedContent(items) {
    const splitItems = _.partition(items, (item) => {
      return !_.isNil(item.props.item.meta.pinned);
    });
    return { pinnedContent: splitItems[0], nonPinnedContent: splitItems[1] };
  }

  /**
   * returns an object where keys are layout tile ids and values are the display item component currently displayed for each layout tile
   *
   * @param currentLayout: {array} layout objects with dimensions and position
   * @param displayItems: {array} display item components
   * @returns {object} { [tileId]: <Medialayer item={item} id={id} moduleOpts={moduleOpts} .../> }
   */
  generateTileMap(layout, displayItems) {
    const items = [...displayItems];
    return _.reduce(
      layout,
      (acc, tile, index) => {
        const pinnedContent = this.getPinnedContent(index);
        if (!_.isEmpty(pinnedContent)) {
          acc[tile.i] = pinnedContent[0]; // always start with first pinned content
        } else if (!_.isEmpty(this.getActivePinnedMedia(tile))) {
          acc[tile.i] = this.generateDisplayItemFromPin(this.getActivePinnedMedia(tile)[0]); // always start with first pinned media
        } else if (!_.isEmpty(items)) {
          acc[tile.i] = items.shift();
        }
        return acc;
      },
      {}
    );
  }

  /**
   * returns a map where keys are pinned tile ids, and values are next pin content idx
   *
   * @param layout: {array} array of tile objects
   * @returns {object} { tileId1: 2, tileId2: 4, ...}
   */
  generateNextPinIdxMap(layout) {
    return _.reduce(
      layout,
      (acc, tile, idx) => {
        const tilePinIds = this.props.moduleOpts.pinsByTileId[tile.i]; // pinned media
        const pinnedContent = this.getPinnedContent(idx);
        if (!_.isEmpty(tilePinIds) || !_.isEmpty(pinnedContent)) {
          const nextPinIdx = 1; // initially showing all first pins in pinned tiles
          acc[tile.i] = nextPinIdx;
        }
        return acc;
      },
      {}
    );
  }

  /**
   * returns a <Medialayer> where item is derived from pinned image or video content
   *
   * @param pin: {object} { id, label, assets, type }
   * @returns {DisplayItem} <Medialayer item={item} id={id} .../>
   */
  generateDisplayItemFromPin(pin) {
    const pinnedItemDisplayItem = new PinnedDisplayItem(
      pin.id,
      { contentType: pin.type, network: 'pinned' },
      { main: pin.assets.main, thumbnail: pin.assets.thumbnail }
    );
    const itemAsset = DisplayItemUtils.getItemAsset({
      item:        pinnedItemDisplayItem,
      assetUrl:    this.props.assetUrl,
      assetUgcUrl: this.props.assetUgcUrl,
      moduleOpts:  this.props.moduleOpts,
      res:         'medium',
    });
    return (
      <Medialayer
        itemAsset={itemAsset}
        assetUrl={this.props.assetUrl}
        assetUgcUrl={this.props.assetUgcUrl}
        templateOpts={{ itemTemplate: 'medialayer', collectionClass: 'collage' }}
        onClick={this.props.onItemClick}
        itemOpts={{}}
        moduleOpts={this.props.moduleOpts}
        item={pinnedItemDisplayItem}
      />
    );
  }

  getCurrentLayout() {
    return this.state.breakpointLayouts[this.state.currentBreakpoint];
  }

  getNextItemIdx() {
    const { nextItemIdx, items } = this.state;

    let currentIdx = nextItemIdx;
    let nextItem = this.getItemByIndex(currentIdx);

    //  iterate through the list of items until we find an item that's not displayed or we have gone through the list
    while (currentIdx - nextItemIdx < items.length && this.isItemDisplayed(nextItem)) {
      currentIdx++;
      nextItem = this.getItemByIndex(currentIdx);
    }
    return currentIdx % items.length;
  }

  getItemByIndex(index) {
    const { items } = this.state;
    return items[index % items.length];
  }

  isItemDisplayed(item) {
    const { tileMap } = this.state;
    return !!_.find(_.values(tileMap), (v) => v === item);
  }

  getNextPin(idx, pins) {
    return pins[idx % pins.length];
  }

  setContainer(node) {
    this.container = node;
  }

  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.Elements.Collections.Custom}
   */
  render() {
    const currentRowHeight = Math.round(this.state.currentWidth / this.state.currentCols);
    const classNames = this.props.className;
    const currentLayout = this.getCurrentLayout();

    // use forEach instead of map to exclude undefined entries in items array
    const items = [];
    _.forEach(this.state.tileMap, (item, tileId) => {
      const tile = _.find(currentLayout, { i: tileId.toString() });
      if (!!tile && !!item) {
        const width = (this.state.currentWidth / this.state.currentCols) * tile.w;
        const height = (this.state.currentWidth / this.state.currentCols) * tile.h;
        const fontSize = `${getProportionalFontSize(width, height)}px`;
        items.push(
          <div className="xItemWrapper" key={tile.i} data-tile-id={tile.i}>
            {React.cloneElement(item, {
              style:         Object.assign({}, item.props.style, { fontSize }),
              customVariant: getVariantByTileSize(width, height),
            })}
          </div>
        );
      }
    });

    /*
      HEADS UP - Creating our own responsive RGL instance here instead of using RGL's <Responsive> b/c of known issues
      when layouts are of varying lengths - one related thread is https://github.com/STRML/react-grid-layout/issues/382
    */
    return (
      <div className={classNames} data-ngx-contentsource="external">
        <div className="xContainerInner" ref={this.setContainer}>
          <ReactGridLayout
            width={this.state.currentWidth}
            rowHeight={currentRowHeight}
            margin={[0, 0]}
            cols={this.state.currentCols}
            layout={currentLayout}
            innerRef={this.containerRef}
          >
            {items}
          </ReactGridLayout>
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} className List of css classnames to apply to the containing element.
 * @property {Object} layouts Array of layout objects that describe collage tiles for each breakpoint.
 */

Custom.propTypes = {
  className: PropTypes.string,
  layouts:   PropTypes.shape({
    lg: PropTypes.arrayOf(
      PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        w: PropTypes.number,
        h: PropTypes.number,
      })
    ),
  }).isRequired,
};

Custom.defaultProps = {
  className: 'xContainer',
};

/**
 * returns breakpoint layout map with added properties to each layout object
 * layout objects must be static, and must have 'i' property for RGL (first iteration tiles did not have 'i' persisted)
 *
 * @param layoutMap: {object} where keys are breakpoints and values are layout arrays
 * @returns {object} { lg: [{x, y, w, h, static, i},....], md: [...] }
 */
function addUIPropsToLayouts(layoutMap) {
  return _.reduce(
    layoutMap,
    (acc, layout, breakpoint) => {
      const staticLayout = layout.map((tile, idx) => {
        return Object.assign({}, tile, { static: true, i: tile.i || idx.toString() });
      });
      acc[breakpoint] = staticLayout;
      return acc;
    },
    {}
  );
}

function getLayoutProps(width) {
  switch (true) {
    case width > BREAKPOINT_THRESHOLDS.lg:
      return { columns: BREAKPOINT_COLUMNS.lg, breakpoint: 'lg' };
    case width > BREAKPOINT_THRESHOLDS.md:
      return { columns: BREAKPOINT_COLUMNS.md, breakpoint: 'md' };
    case width > BREAKPOINT_THRESHOLDS.sm:
      return { columns: BREAKPOINT_COLUMNS.sm, breakpoint: 'sm' };
    case width > BREAKPOINT_THRESHOLDS.xs:
    default:
      return { columns: BREAKPOINT_COLUMNS.xs, breakpoint: 'xs' };
  }
}

/**
- * returns an array of 1:1 layout objects of defined length
- *
- * @param length: {number} the length of the collage items
- * @returns {array} [{x, y, w, h},....]
- */
function generateSmallLayout(length, columnCount = 1) {
  let count = 0;
  const result = [];
  while (count < length) {
    result.push({
      x: 0,
      y: count * columnCount,
      w: columnCount,
      h: columnCount,
    });
    count += 1;
  }
  return result;
}

/**
 * returns an array of layout objects with length greater than or equal to defined length
 * set of 5 different layout objects that repeat
 * number of layout objects returned will be >= defined length
 *
 * @param length: {number} the length of the collage items
 * @returns {array} [{x, y, w, h},....]
 */
function generateMediumLayout(length, columnCount) {
  let result = [];
  let count = 0;
  while (count < length / 5) {
    result = result.concat([
      {
        x: 0,
        y: count * (columnCount * 2),
        w: columnCount,
        h: columnCount,
      },
      {
        x: 0,
        y: columnCount + count * columnCount * 2,
        w: columnCount / 2,
        h: columnCount / 2,
      },
      {
        x: columnCount / 2,
        y: columnCount + count * (columnCount * 2),
        w: columnCount / 2,
        h: columnCount / 2,
      },
      {
        x: 0,
        y: columnCount + columnCount / 2 + count * (columnCount * 2),
        w: columnCount / 2,
        h: columnCount / 2,
      },
      {
        x: columnCount / 2,
        y: columnCount + columnCount / 2 + count * (columnCount * 2),
        w: columnCount / 2,
        h: columnCount / 2,
      },
    ]);
    count += 1;
  }
  return _.take(result, length);
}

/**
- * returns a sorted layout list for pinned content association
- * sorted from top left of coord plane to bottom right
- *
- * @param layout the layout to sort [{ x: 6, y: 12}, {x: 0, y: 12}]
- * @returns {array} [{ x: 0, y:12, x: 6, y: 12 }]
- */
const sortLayoutByCoords = (layout) => {
  return layout.sort((tileA, tileB) => {
    if (tileA.y === tileB.y) {
      return tileA.x - tileB.x;
    } else {
      return tileA.y - tileB.y;
    }
  });
};

const _test = {
  addUIPropsToLayouts,
};

export { Custom, _test };
